import { Stack, Typography } from '@mui/joy';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Color, Direction, Level } from 'theme/enums';

export interface IExpandableValueListItem {
  id: number;
  name: string;
  sublineLabel?: string;
  sublineValue?: string | null;
}

interface IProps {
  items: IExpandableValueListItem[];
  moreLabel: string;
}

const MAX_VISIBLE_ITEMS = 3;

export const ExpandableValueList = ({ items, moreLabel }: IProps) => {
  const [showAllItems, setShowAllItems] = useState(false);

  const { t } = useTranslation();

  const hiddenItemsCount = items.length - MAX_VISIBLE_ITEMS;
  const hasHiddenItems = hiddenItemsCount > 0;
  const visibleItems = showAllItems ? items : items.slice(0, MAX_VISIBLE_ITEMS);

  const onToggle = () => setShowAllItems((prev) => !prev);

  return (
    <Stack direction={Direction.COLUMN} sx={{ alignItems: 'flex-end', gap: 0.5 }}>
      {visibleItems.map((item) => (
        <Stack key={item.id} direction={Direction.COLUMN} sx={{ alignItems: 'flex-end' }}>
          <Typography level={Level.BODY_SM} sx={{ fontWeight: 600 }}>
            {item.name}
          </Typography>

          {item.sublineLabel && item.sublineValue && (
            <Typography level={Level.BODY_XS} color={Color.NEUTRAL}>
              {item.sublineLabel}: {item.sublineValue}
            </Typography>
          )}
        </Stack>
      ))}

      {hasHiddenItems && (
        <Typography
          component="button"
          type="button"
          color={Color.PRIMARY}
          level={Level.BODY_SM}
          sx={{
            border: 'none',
            background: 'none',
            padding: 0,
            cursor: 'pointer',
            '&:hover': { fontWeight: 600 },
          }}
          onClick={onToggle}
        >
          {showAllItems
            ? t('labelShowLess')
            : t('labelMoreCountTyped', { count: hiddenItemsCount, type: moreLabel })}
        </Typography>
      )}
    </Stack>
  );
};
